import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { Seo } from '@/layout/Seo'
import { useMetaForProject } from '@/layout/Seo/ProjectSeoHelper'
import { useCanonicalUrl } from '@/layout/Seo/useCanonicalUrl'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { Downloadable, getPressPageContentByProject, PressRelease, Project, TeamMember } from '@/services/PressService'
import { getProjectAbout, ProjectMeta } from '@/services/ProjectsService'
import { EmbeddedVideo } from '@/types/codegen-contentful'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { noPreRenderedStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PressView } from '@/views/PressView'

interface PressProjectPageProps {
  locale: string
  downloadables: Downloadable[]
  pressReleases: PressRelease[]
  project: Project
  projectMeta: ProjectMeta
  teamMembers: TeamMember[]
  videos: EmbeddedVideo[]
}

const PressProjectPage: NextPageWithLayout<PressProjectPageProps> = ({
  locale,
  downloadables,
  pressReleases,
  project,
  projectMeta,
  teamMembers,
  videos,
}) => {
  const meta = useMetaForProject({ ...projectMeta, locale })

  const url = useCanonicalUrl(`${paths.press.index}/${project.slug}`)
  const title = `${project.name} Press | Angel`
  const description = `${project.name} Press Room`

  return (
    <>
      <Seo {...meta} canonical={url} title={title} description={description} />
      <PressView
        downloadables={downloadables}
        pressReleases={pressReleases}
        project={project}
        teamMembers={teamMembers}
        videos={videos}
      />
    </>
  )
}

PressProjectPage.getLayout = (page) => {
  return <DefaultLayout isDarkMode={false}>{page}</DefaultLayout>
}

export const getStaticPaths = noPreRenderedStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<PressProjectPageProps>(
  { filePath: 'pages/[locale]/press/[project].tsx' },
  async ({ federationClient, locale, next404, params, preview }) => {
    const projectSlug = getItemOrFirstEntry(params?.project)
    if (!projectSlug) return next404()

    if (projectSlug === slugs.dryBar) {
      return {
        redirect: {
          destination: paths.press.dryBarComedy,
          permanent: false,
        },
      }
    }

    if (projectSlug === slugs.truthAndTreasonSeries || projectSlug === slugs.truthAndConviction) {
      return {
        redirect: {
          destination: paths.press.truthAndTreason,
          permanent: false,
        },
      }
    }

    const [pageContent, projectMeta, translations] = await Promise.all([
      getPressPageContentByProject(preview, projectSlug, locale),
      getProjectAbout({ slug: projectSlug }, federationClient, {
        locale,
        preview,
      }),
      loadTranslations(locale, ['common', 'account', 'watch']),
    ])

    const { downloadables, pressReleases, project, teamMembers, videos } = pageContent

    if (!project.hasPressPage) {
      return next404(Durations.FIVE_MINUTES_IN_SECONDS)
    }

    if (!projectMeta) {
      return next404()
    }

    return {
      props: {
        locale,
        downloadables,
        pressReleases,
        project,
        projectMeta,
        teamMembers,
        videos,
        ...translations,
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export default PressProjectPage
